import React, { createContext, useContext, useState, useCallback } from 'react';
import { ProcessingItem } from './UploadContext';

interface GenerationProgressState {
  isGenerating: boolean;
  total: number;
  completed: number;
  errors: number;
  templateName: string;
  items: ProcessingItem[];
}

interface GenerationContextType extends GenerationProgressState {
  updateProgress: (update: Partial<GenerationProgressState>) => void;
  setItems: (items: ProcessingItem[] | ((prev: ProcessingItem[]) => ProcessingItem[])) => void;
  resetGeneration: () => void;
  closeProgress: () => void;
}

const GenerationContext = createContext<GenerationContextType | null>(null);

export const GenerationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [total, setTotal] = useState(0);
  const [completed, setCompleted] = useState(0);
  const [errors, setErrors] = useState(0);
  const [templateName, setTemplateName] = useState('');
  const [items, setItemsState] = useState<ProcessingItem[]>([]);

  const updateProgress = useCallback((update: Partial<GenerationProgressState>) => {
    if (update.isGenerating !== undefined) setIsGenerating(update.isGenerating);
    if (update.total !== undefined) setTotal(update.total);
    if (update.completed !== undefined) setCompleted(update.completed);
    if (update.errors !== undefined) setErrors(update.errors);
    if (update.templateName !== undefined) setTemplateName(update.templateName);
  }, []);

  const setItems = useCallback((itemsOrFn: ProcessingItem[] | ((prev: ProcessingItem[]) => ProcessingItem[])) => {
    if (typeof itemsOrFn === 'function') {
      setItemsState(itemsOrFn);
    } else {
      setItemsState(itemsOrFn);
    }
  }, []);

  const resetGeneration = useCallback(() => {
    setIsGenerating(false);
    setTotal(0);
    setCompleted(0);
    setErrors(0);
    setTemplateName('');
    // Don't clear items — let user see final state until they close
  }, []);

  const closeProgress = useCallback(() => {
    setItemsState([]);
    setIsGenerating(false);
    setTotal(0);
    setCompleted(0);
    setErrors(0);
    setTemplateName('');
  }, []);

  return (
    <GenerationContext.Provider
      value={{
        isGenerating,
        total,
        completed,
        errors,
        templateName,
        items,
        updateProgress,
        setItems,
        resetGeneration,
        closeProgress,
      }}
    >
      {children}
    </GenerationContext.Provider>
  );
};

export const useGeneration = () => {
  const context = useContext(GenerationContext);
  if (!context) {
    throw new Error('useGeneration must be used within GenerationProvider');
  }
  return context;
};
